import React, { FC, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import {
  getReleasePeriod,
  HotelReleaseGroupEntries,
  HotelReservationStatus,
  PublicHotelReleaseGroup,
  ReleaseForceEntry,
  ReleaseGroupStage,
  ReleaseGroupType,
} from '../../../shared/hotel';
import { ExternalLinkButton, MaterialIcon, TermsLoader } from '../../components';
import { CopyButton } from '../../components/CopyButton';
import { MaterialIconText } from '../../components/MaterialIconText';
import { useTranslation } from '../../translations';
import { useTimeoutCreator, useToggle } from '../../utils';
import { captureError } from '../../utils/errorHandling';

interface Props {
  readonly wave: PublicHotelReleaseGroup;
}

interface WaveEntryProps extends Props {
  readonly entry: HotelReleaseGroupEntries | null;
  readonly refresh: () => void;
}

const dateFormat: Intl.DateTimeFormatOptions = {
  timeZoneName: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

export const WaveEntry: FC<WaveEntryProps> = ({ entry, wave, refresh }) => {
  const period = getReleasePeriod(wave, new Date());

  if (!entry && wave.forceEntry !== ReleaseForceEntry.None) {
    return <WaveAutoEntry />;
  }

  if (!entry) {
    if (
      (wave.type !== ReleaseGroupType.FirstComeFirstServe &&
        period !== ReleaseGroupStage.EntryPeriod) ||
      (wave.type === ReleaseGroupType.FirstComeFirstServe &&
        ![ReleaseGroupStage.EntryPeriod, ReleaseGroupStage.ReleasePeriod].includes(period))
    ) {
      return <WaveClosed wave={wave} />;
    }

    return <WaveEnter refresh={refresh} wave={wave} />;
  }

  if (entry.isSelected && entry.reservation) {
    const { reservation } = entry;

    if (reservation.hasUrl && reservation.status === HotelReservationStatus.Pending) {
      return <WaveLink entry={entry} wave={wave} />;
    }

    if (reservation.status === HotelReservationStatus.Cancelled) {
      return <WaveReEntry refresh={refresh} wave={wave} />;
    }

    if (reservation.status === HotelReservationStatus.Confirmed) {
      return <WaveBooked entry={entry} />;
    }
  }

  return <WaveLeave entry={entry} refresh={refresh} wave={wave} />;
};

interface ExtProps extends Props {
  readonly refresh: () => void;
}

const WaveEnter: FC<ExtProps> = ({ wave, refresh }) => {
  const [termsAccepted, toggleTermsAccepted] = useToggle(false);
  const { ts } = useTranslation();

  const enterWave = useCallback(async () => {
    if (!termsAccepted) {
      return;
    }

    try {
      await api.createReleaseEntry(wave.id, termsAccepted);
      toast.success(ts('you_have_successfully_entered_this_wave'));
      refresh();
    } catch (error) {
      captureError(error as Error);
    }
  }, [termsAccepted, wave.id, ts, refresh]);

  const { eligibility } = wave;

  if (!eligibility.isEligible) {
    return (
      <Card>
        <CardBody>
          <Row>
            <Col className="text-center" xs={2}>
              <MaterialIcon className="text-danger" large name="event_busy" />
            </Col>
            <Col xs={10}>
              <h4>{ts('not_eligible_to_enter')}</h4>
              <hr />
              <p>{ts('you_currently_do_not_meet_the_criteria_to_enter_this_wave')}</p>
              <p>
                {ts('if_you_believe_this_to_be_in_error_please_contact_the_event_for_assistance')}
              </p>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <h4>{ts('enter_wave')}</h4>
        <hr />
        {wave.type === ReleaseGroupType.FirstComeFirstServe ? (
          <>
            <p>
              {ts(
                'hotel.this_wave_uses_a_first_come_first_serve_queueing_system_when_the_booking_period_begins_entries_will_be_processed_from_the_queue_in_order_of_entry',
              )}
            </p>
            <p>
              {ts(
                'hotel.when_your_entry_is_reaches_the_front_of_the_queue_and_is_processed_this_page_will_update_with_your_link_and_you_will_receive_a_notification_by_email_with_your_link_to_book',
              )}
            </p>
          </>
        ) : (
          <>
            <p>
              {ts(
                'hotel.this_wave_uses_a_random_drawing_system_all_accepted_entries_prior_to_the_entry_period_closing_will_be_randomized_and_winners_drawn_from_the_pool_of_entries',
              )}
            </p>
            <p>
              {ts(
                'if_you_are_selected_to_reserve_a_room_this_page_will_update_with_your_link_and_you_will_receive_a_notification_by_email_with_your_link_to_book',
              )}
            </p>
          </>
        )}
        <hr />
        <p>
          {ts(
            'hotel.before_you_can_enter_this_wave_you_must_read_and_agree_to_the_events_hotel_reservation_policy',
          )}
        </p>
        <Alert color="info" fade={false}>
          <TermsLoader name="hotelReservationTerms" />
        </Alert>
        <hr />
        <FormGroup check className="mt-10 mb-3">
          <Input
            id="termsAccepted"
            name="termsAccepted"
            onChange={toggleTermsAccepted}
            type="checkbox"
          />
          <Label check for="termsAccepted">
            {ts('i_have_read_and_agree_to_the_hotel_reservation_policy')}
          </Label>
        </FormGroup>
        <Button
          block
          color="primary"
          disabled={!termsAccepted}
          id="enterWaveButton"
          onClick={enterWave}
        >
          {ts('enter_wave')}
        </Button>
      </CardBody>
    </Card>
  );
};

interface WaveLinkProps extends Props {
  readonly entry: HotelReleaseGroupEntries;
}

const WaveLink: FC<WaveLinkProps> = ({ entry, wave }) => {
  const { ts } = useTranslation();

  if (!entry.reservation?.bookingUrl) {
    return (
      <Card>
        <CardBody>
          <h4>
            <MaterialIconText medium name="error" type="danger">
              {ts('common.error')}
            </MaterialIconText>
          </h4>
          <hr />
          <p>{ts('hotel.booking_link_error')}</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <h4>
          <MaterialIconText medium name="done" type="success">
            {ts('youre_ready_to_book')}
          </MaterialIconText>
        </h4>
        <hr />
        <p>
          {ts(
            wave.type === ReleaseGroupType.FirstComeFirstServe
              ? 'your_entry_has_been_processed_and_your_unique_reservation_link_is_ready'
              : 'congratulations_youve_been_randomly_selected_to_book_a_room',
          )}
        </p>
        <p>
          {ts(
            'hotel.room_booking_deadline_message',
            wave.releaseEndAt.toLocaleString(undefined, dateFormat),
          )}
        </p>
        <Alert className="mb-3" color="warning">
          <p>
            <strong>{ts('common.important')}</strong> {ts('hotel.link_share_warning')}
          </p>
        </Alert>
        <p>{ts('click_the_button_below_to_be_transferred_to_the_hotel_reservation_system')}</p>
        <ButtonGroup className="w-100">
          <ExternalLinkButton block color="primary" href={entry.reservation.bookingUrl}>
            {ts('proceed_to_booking_platform')}
          </ExternalLinkButton>
          <CopyButton
            color="secondary"
            textToCopy={entry.reservation.bookingUrl}
            toastMessage="hotel.booking_link_copied_to_clipboard"
          />
        </ButtonGroup>
      </CardBody>
    </Card>
  );
};

interface WaveBookedProps {
  readonly entry: HotelReleaseGroupEntries;
}

const WaveBooked: FC<WaveBookedProps> = ({ entry }) => {
  const history = useHistory();
  const { ts } = useTranslation();

  const onClick = useCallback(() => {
    history.push(`/hotel/reservation/${entry.reservation!.id}`);
  }, [entry.reservation, history]);

  return (
    <Card>
      <CardBody>
        <h4>
          <MaterialIconText medium name="done" type="success">
            {ts('booking_completed')}
          </MaterialIconText>
        </h4>
        <hr />
        <p>
          {ts(
            'youve_successfully_reserved_a_room_and_your_reservation_number_is',
            entry.reservation!.extReservationId ?? entry.reservation!.id,
          )}
        </p>
        <p>{ts('hotel.wave.modify_res_help')}</p>
        <hr />
        <Button block color="primary" onClick={onClick} outline>
          {ts('go_to_my_reservation')}
        </Button>
      </CardBody>
    </Card>
  );
};

const WaveReEntry: FC<ExtProps> = ({ wave, refresh }) => {
  const enterWave = useCallback(async () => {
    try {
      await api.createReleaseEntry(wave.id, true);
      setTimeout(() => {
        refresh();
      }, 250);
    } catch (error) {
      captureError(error as Error);
    }
  }, [wave, refresh]);

  const { ts } = useTranslation();

  return (
    <Card>
      <CardBody>
        <h4>
          <MaterialIconText medium name="info" type="info">
            {ts('youre_eligible_to_reenter')}
          </MaterialIconText>
        </h4>
        <hr />
        <p>
          {ts(
            'it_looks_like_you_already_reserved_a_room_in_this_wave_but_the_reservation_was_cancelled',
          )}
        </p>
        <p>
          {ts(
            'hotel.as_you_are_still_eligible_to_reserve_a_room_in_this_wave_you_can_click_the_button_below_to_reenter_and_request_a_new_reservation_link',
          )}
        </p>
        <hr />
        <Button block color="primary" onClick={enterWave} outline>
          {ts('reenter_wave')}
        </Button>
      </CardBody>
    </Card>
  );
};

interface WaveLeaveProps extends ExtProps {
  readonly entry: HotelReleaseGroupEntries;
}

const WaveLeave: FC<WaveLeaveProps> = ({ entry, wave, refresh }) => {
  const { ts } = useTranslation();
  const leaveWave = useCallback(async () => {
    try {
      await api.deleteReleaseEntry(entry.id);
      toast.success(ts('you_have_successfully_withdrawn_your_entry_from_this_wave'));
      refresh();
    } catch (error) {
      captureError(error as Error);
    }
  }, [entry.id, ts, refresh]);

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <h4 className="mb-3">{ts('youve_entered_this_wave')}</h4>
          {wave.type === ReleaseGroupType.FirstComeFirstServe ? (
            <FCFSQueue refresh={refresh} wave={wave} />
          ) : (
            <Alert className="mb-0" color="info">
              <p>
                {ts('hotel.wave.entered')}{' '}
                <strong>{wave.releaseStartAt.toLocaleString(undefined, dateFormat)}</strong>.
              </p>
              <p>
                {ts('if_you_are_selected_in_the_draw_you_will_have_until')}{' '}
                <strong>{wave.releaseEndAt.toLocaleString(undefined, dateFormat)}</strong>{' '}
                {ts('hotel.wave.to_book_your_room')}.
              </p>
            </Alert>
          )}
        </CardBody>
      </Card>
      {wave.forceEntry === ReleaseForceEntry.None ? (
        <Card className="mb-3">
          <CardBody>
            <h4>{ts('leave_wave')}</h4>
            <hr />
            <p>
              {ts(
                'hotel.if_you_no_longer_wish_to_participate_in_this_wave_you_may_leave_it_at_any_time_before_the_entry_period_ends',
              )}
            </p>
            <hr />
            <Button block color="danger" onClick={leaveWave} outline>
              {ts('leave_wave')}
            </Button>
          </CardBody>
        </Card>
      ) : null}
    </>
  );
};

const FCFSQueue: FC<ExtProps> = ({ refresh, wave }) => {
  const { ts } = useTranslation();

  if (getReleasePeriod(wave, new Date()) !== ReleaseGroupStage.ReleasePeriod) {
    return (
      <Alert color="info">
        <p>
          <strong>{ts('you_have_successfully_entered_this_wave')}</strong>{' '}
          {ts('hotel.wave.fcfs.pre_release_explanation')}
        </p>
      </Alert>
    );
  }

  return (
    <>
      <Alert color="info">
        <p>
          <strong>{ts('you_have_successfully_entered_this_wave')}</strong>{' '}
          {ts('hotel.wave.auto_refresh')}
        </p>
      </Alert>
      <FCFSRefresh refresh={refresh} />
    </>
  );
};

const FCFSRefresh: FC<{ readonly refresh: () => void }> = ({ refresh }) => {
  const timeoutCreator = useTimeoutCreator();
  const [seconds, setSeconds] = useState(30);
  const { ts } = useTranslation();

  timeoutCreator(() => {
    if (seconds === 1) {
      refresh();
    } else if (seconds > 1) {
      setSeconds(seconds - 1);
    }
  }, 1000);

  return (
    <p className="text-center text-secondary">
      <small>
        <strong>{ts('your_queue_status_will_automatically_refresh_in_seconds', seconds)}</strong>
      </small>
    </p>
  );
};

const WaveClosed: FC<Props> = ({ wave }) => {
  const { ts } = useTranslation();

  return (
    <Card>
      <CardBody>
        <Row>
          <Col className="text-center" xs={2}>
            <MaterialIcon className="text-danger" large name="event_busy" />
          </Col>
          <Col xs={10}>
            <h4>{ts('wave_closed')}</h4>
            <hr />
            <p>{ts('this_wave_is_currently_closed_for_entries_at_this_time')}</p>
            <p>
              {ts(
                wave.type === ReleaseGroupType.FirstComeFirstServe
                  ? 'please_check_back_when_the_booking_period_starts_to_enter_the_queue'
                  : 'hotel.please_check_the_hotel_reservations_main_page_for_other_waves_that_may_be_available_for_entries',
              )}
            </p>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const WaveAutoEntry: FC = () => {
  const { ts } = useTranslation();

  return (
    <Card>
      <CardBody>
        <Row>
          <Col className="text-center" xs={2}>
            <MaterialIcon className="text-info" large name="smart_toy" />
          </Col>
          <Col xs={10}>
            <h4>{ts('automatic_entries')}</h4>
            <hr />
            <p>
              <strong>{ts('this_wave_uses_an_automatic_entry_system')}.</strong>{' '}
              {ts('hotel.wave.auto_entry')}
            </p>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};
